import { Guid, IBaseModel } from '~/interfaces/base'
import { SalaryItemDataTypes, SalaryItemActionTypes, SalaryItemFillTypes, SalaryItemTypes } from '~/enums/salary'
import { Personnel } from '@/classes/salary/personnel'
import emptyId from '@/statics/empty-id'
import { i18nInstance } from '@/plugins/localization'
import { BaseState } from '~/classes/system/grid'
import { RowStates } from '~/enums/system'

export class SalaryItem implements BaseState {
	constructor() {
	}

	id: Guid = emptyId
	companyId!: Guid
	name!: string
	displayName!: string
	accountId!: Guid
	dataType!: SalaryItemDataTypes
	actionType!: SalaryItemActionTypes
	fillType!: SalaryItemFillTypes
	type!: SalaryItemTypes
	formula!: string
	jsonFormula: any = null
	defaultValue!: string | number
	maxValue!: number
	includeInsurance: boolean = false
	includeTax: boolean = false
	rowState!: RowStates
	isActive: boolean = true
	code?: string
	resultFunctionCode?: number
	continuous: boolean = false
	nonCash: boolean = false
	usedSalaryItemIds!: string
	systemKey!: string
	orderIndex: number = 0
	ignoreNonPrivilegeCalc: boolean = false
}

export class MinimalSalaryItem implements IBaseModel {
	id: Guid
	name: string
	value: string

	constructor() {
		this.id = emptyId
		this.name = ''
		this.value = ''
	}
}

export class SelectedDataType {
	constructor(init?: Partial<SelectedDataType>) {
		Object.assign(this, init)
	}

	id!: SalaryItemDataTypes
	name!: string
}

export class SelectedDataEntry {
	constructor(init?: Partial<SelectedDataEntry>) {
		Object.assign(this, init)
	}

	id!: SalaryItemFillTypes
	name!: string
	class!: string
}

export class SelectedItemType {
	constructor(init?: Partial<SelectedItemType>) {
		Object.assign(this, init)
	}

	id!: SalaryItemTypes
	name!: string
	class!: string
}

export class SelectedFunctionType {
	constructor(init?: Partial<SelectedFunctionType>) {
		Object.assign(this, init)
	}

	id!: SalaryItemActionTypes
	name!: string
	class!: string
	icon!: string
}

export const itemTypes: SelectedItemType[] = [
	// @ts-ignore
	{ id: SalaryItemTypes.Deductions, name: i18nInstance.global.t('salary.item.deductions'), class: 'salary-item-light-yellow' },
	{ id: SalaryItemTypes.Privilege, name: i18nInstance.global.t('salary.item.privilege'), class: 'salary-item-light-green' },
	{ id: SalaryItemTypes.Salary, name: i18nInstance.global.t('salary.item.salary'), class: 'salary-item-light-red' },
	{ id: SalaryItemTypes.Functional, name: i18nInstance.global.t('salary.item.functional',), class: 'salary-item-light-purple' },
]
export const entryTypes: SelectedDataEntry[] = [
	{ id: SalaryItemFillTypes.Monthly, name: i18nInstance.global.t('salary.item.monthly'), class: 'salary-item-green' },
	{ id: SalaryItemFillTypes.Yearly, name: i18nInstance.global.t('salary.item.yearly'), class: 'salary-item-pink' },
	{ id: SalaryItemFillTypes.EndYear, name: i18nInstance.global.t('salary.item.endYear'), class: 'salary-item-pink' },
]
export const operationTypes: SelectedFunctionType[] = [
	{ id: SalaryItemActionTypes.Computational, name: i18nInstance.global.t('salary.item.computational'), class: 'salary-item-blue2', icon: 'fa-solid fa-calculator' },
	{ id: SalaryItemActionTypes.Display, name: i18nInstance.global.t('salary.item.display'), class: 'salary-item-purple', icon: 'fa-solid fa-eye' },
	{ id: SalaryItemActionTypes.Single, name: i18nInstance.global.t('salary.item.single'), class: 'salary-item-yellow', icon: 'fa-solid fa-keyboard' },
]
export const dataTypes: SelectedDataType[] = [
	{ id: SalaryItemDataTypes.Date, name: i18nInstance.global.t('salary.item.date') },
	{ id: SalaryItemDataTypes.Money, name: i18nInstance.global.t('salary.item.money') },
	{ id: SalaryItemDataTypes.Numerical, name: i18nInstance.global.t('salary.item.numerical') },
	{ id: SalaryItemDataTypes.String, name: i18nInstance.global.t('salary.item.string') },
	{ id: SalaryItemDataTypes.Time, name: i18nInstance.global.t('salary.item.time') },
]

export class ConvSalaryItem extends SalaryItem {
	setParentVariables() {
		if (this._selectedDataType?.id) (this.dataType = this._selectedDataType.id)
		if (this._selectedFunctionType?.id) this.actionType = this._selectedFunctionType.id
		if (this._selectedDataEntry?.id) this.fillType = this._selectedDataEntry.id
		if (this._selectedItemType?.id) this.type = this._selectedItemType.id
		if (this._selectedAccount?.id) this.accountId = this._selectedAccount.id
	}

	constructor(init?: Partial<ConvSalaryItem>) {
		super()
		Object.assign(this, init)
		this._selectedDataType = this.dataType ? dataTypes.find(x => x.id === this.dataType) : dataTypes[2]
		this._selectedFunctionType = this.actionType ? operationTypes.find(x => x.id === this.actionType) : null
		this._selectedItemType = this.type ? itemTypes.find(x => x.id === this.type) : null
		this._selectedDataEntry = this.fillType ? entryTypes.find(x => x.id === this.fillType) : null
		this._selectedAccount = this.accountId ? { id: this.accountId } : null
		//if (typeof this.jsonFormula === 'string') this.jsonFormula = this.jsonFormula
	}

	private _selectedDataType: SelectedDataType | null | undefined = null
	private _selectedFunctionType: SelectedFunctionType | null | undefined = null
	private _selectedItemType: SelectedItemType | null | undefined = null
	private _selectedDataEntry: SelectedDataEntry | null | undefined = null
	private _selectedAccount: any = null

	get selectedDataType() {
		return this._selectedDataType
	}

	set selectedDataType(data: typeof this._selectedDataType) {
		this._selectedDataType = data
		this.setParentVariables()
	}

	get selectedFunctionType() {
		return this._selectedFunctionType
	}

	set selectedFunctionType(data: typeof this._selectedFunctionType) {
		this._selectedFunctionType = data
		this.setParentVariables()
	}

	get selectedItemType() {
		return this._selectedItemType
	}

	set selectedItemType(data: typeof this._selectedItemType) {
		this._selectedItemType = data
		this.setParentVariables()
	}

	get selectedDataEntry() {
		return this._selectedDataEntry
	}

	set selectedDataEntry(data: typeof this._selectedDataEntry) {
		this._selectedDataEntry = data
		this.setParentVariables()
	}

	get selectedAccount() {
		return this._selectedAccount
	}

	set selectedAccount(data: typeof this._selectedAccount) {
		this._selectedAccount = data
		this.setParentVariables()
	}
}

export class PersonSalaryItems extends Personnel {
	items!: SalaryItem[]
}